// TeamDetailLive.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";
import moment from "moment";
import Header from "./components/Header/Header";
import SeasonRangeFilter from "./components/SeasonRangeFilter/SeasonRangeFilter";
import HistoricalInfoBoxes from "./components/HistoricalInfoBoxes/HistoricalInfoBoxes";
import { useParams, useNavigate } from "react-router-dom";
import { useGender } from "contexts/GenderContext";
import RankPointsChart from "./components/RankPointsChart/RankPointsChart";
import RankPositionChart from "./components/RankPositionChart/RankPositionChart";
import fetchWorldRanking from "hooks/getWorldRanking";
import fetchTeamStatsAggregated from "hooks/getTeamStatsAggregated";
import fetchTeamTable from "hooks/getTeamTable";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import MatchesPlayTable from "./components/MatchesPlayTable/MatchesPlayTable";
import MatchesPlayTableMobile from "./components/MatchesPlayTable/MatchesPlayTableMobile";
import MatchProbTable from "./components/MatchProbabilitiesTable/MatchProbabilitiesTable";
import MatchProbTableMobile from "./components/MatchProbabilitiesTable/MatchProbabilitiesTableMobile";
import appConfig from "appConfig";
import {
  getAvailableYears,
  getSeasonsFromChartData,
  getChartDateBounds,
  getDateHistoryRange,
  getDateRangeForPreset,
  findActivePresetForDateRange,
  resolveDateSelection,
  formatDateRangeLabel,
  filterChartDataByHistoryRange,
  computeNetWrChange,
  computeRankChange,
} from "./utils/teamHistoryFilter";
import {
  computePointsToNextRank,
  computePointsToFirst,
  computeCurrentSeasonWinRate,
} from "./utils/teamKpiMetrics";
import ExtractReportModal from "components/ExtractReportModal/ExtractReportModal";
import FIVBLogo2 from "assets/images/icons/fivb-logo-small.svg";
import {
  exportTeamHistoryToXlsx,
  exportTeamHistoryToPdf,
  waitForNextPaint,
  waitForImagesToSettle,
} from "./utils/teamHistoryExport";
import "assets/styles.css";

const TeamDetailLive = () => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const { selectedGender } = useGender();
  const { instance, accounts } = useMsal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const matchTableRef = useRef(null);
  const exportCaptureRef = useRef(null);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isMatchDataLoaded, setIsMatchDataLoaded] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [exportErrorMessage, setExportErrorMessage] = useState("");
  const [isPdfExportMode, setIsPdfExportMode] = useState(false);

  const [rank, setRank] = useState("N/A");
  const [points, setPoints] = useState("N/A");
  const [confederationName, setConfederationName] = useState("N/A");
  const [confederationCode, setConfederationCode] = useState("N/A");
  const [error, setError] = useState(null);
  const [hasTeamData, setHasTeamData] = useState(true);
  const [worldRankingData, setWorldRankingData] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [chartSourceData, setChartSourceData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const genderValue = selectedGender === "Men" ? 1 : 0;
  const [apiDate] = useState(moment().format("YYYY-MM-DD"));

  const availableSeasons = useMemo(() => {
    const seasons = new Set([
      ...getAvailableYears(teamStats),
      ...getSeasonsFromChartData(chartSourceData),
    ]);
    return [...seasons].sort((a, b) => b - a);
  }, [teamStats, chartSourceData]);

  const dateBounds = useMemo(
    () => getChartDateBounds(chartSourceData),
    [chartSourceData]
  );

  const historyRange = useMemo(
    () => getDateHistoryRange(fromDate, toDate),
    [fromDate, toDate]
  );

  const kpiMetrics = useMemo(
    () => ({
      pointsToNextRank: computePointsToNextRank(
        worldRankingData,
        rank,
        points
      ),
      pointsToFirst: computePointsToFirst(worldRankingData, rank, points),
      winRate: computeCurrentSeasonWinRate(teamStats),
    }),
    [worldRankingData, rank, points, teamStats]
  );

  const filteredChartData = useMemo(() => {
    const pointsData = chartSourceData.map((item) => ({
      date: moment(item.dateTimeUtc).format("YYYY-MM-DD"),
      value: parseFloat(item.n_NewWRS_target).toFixed(2),
    }));

    const rankData = chartSourceData.map((item) => ({
      date: moment(item.dateTimeUtc).format("YYYY-MM-DD"),
      value: item.n_WRanking,
    }));

    return {
      points: filterChartDataByHistoryRange(pointsData, historyRange),
      rank: filterChartDataByHistoryRange(rankData, historyRange),
    };
  }, [chartSourceData, historyRange]);

  const netWrChange = useMemo(
    () => computeNetWrChange(filteredChartData.points),
    [filteredChartData.points]
  );

  const rankChange = useMemo(
    () => computeRankChange(filteredChartData.rank),
    [filteredChartData.rank]
  );

  const getAccessToken = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error("No account found");
    }

    const request = {
      scopes: appConfig.authScopes,
      account: accounts[0],
    };

    const accessTokenResponse = await instance.acquireTokenSilent(request);
    return accessTokenResponse.accessToken;
  }, [accounts, instance]);

  const getTeamCode = useCallback(() => {
    const teamEntry = teamCodeHashmap[teamName.toLowerCase()];
    return teamEntry ? Object.keys(teamEntry)[0] : "";
  }, [teamName]);

  const fetchTeamRankAndPoints = async () => {
    try {
      setIsPageLoading(true);
      setError(null);
      setRank("N/A");
      setPoints("N/A");
      setConfederationName("N/A");
      setConfederationCode("N/A");
      setHasTeamData(true);
      setWorldRankingData([]);
      setTeamStats([]);
      setChartSourceData([]);
      setFromDate(null);
      setToDate(null);

      const accessToken = await getAccessToken();
      const teamCode = getTeamCode();

      if (!teamCode) {
        setHasTeamData(false);
        return;
      }

      const [worldRankingResult, statsResult, tableResult] = await Promise.all([
        fetchWorldRanking(apiDate, genderValue, accessToken),
        fetchTeamStatsAggregated(teamCode, genderValue, accessToken),
        fetchTeamTable(teamCode, apiDate, genderValue, -1, 0, accessToken),
      ]);

      setWorldRankingData(worldRankingResult || []);
      setTeamStats(statsResult || []);

      const sortedChartData = (tableResult || [])
        .filter((item) => {
          const dateValue = moment(item.dateTimeUtc, moment.ISO_8601, true);
          return item.dateTimeUtc && dateValue.isValid();
        })
        .sort(
          (a, b) =>
            moment(a.dateTimeUtc).toDate() - moment(b.dateTimeUtc).toDate()
        );
      setChartSourceData(sortedChartData);

      const teamData = worldRankingResult.find(
        (item) =>
          item.team_CalculatedCode_target.toLowerCase() ===
          teamCode.toLowerCase()
      );

      if (teamData) {
        setRank(teamData.n_WRanking);
        setPoints(parseFloat(teamData.f_WRS).toFixed(2));
        setConfederationName(teamData.confederation_name_target);
        setConfederationCode(teamData.confederation_code_target);
        setHasTeamData(true);
      } else {
        setHasTeamData(false);
      }
    } catch (fetchError) {
      if (fetchError instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect({
          scopes: appConfig.authScopes,
          account: accounts[0],
        });
      } else {
        setError(fetchError.message);
        setHasTeamData(false);
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamRankAndPoints();
    setIsMatchDataLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, selectedGender]);

  useEffect(() => {
    setIsMatchDataLoaded(false);
  }, [historyRange]);

  // Initialize the date range once the data date bounds are known (defaults to
  // the full available history), and keep the current selection clamped.
  useEffect(() => {
    if (!dateBounds) return;

    const resolved = resolveDateSelection(dateBounds, fromDate, toDate);

    if (!fromDate || fromDate.getTime() !== resolved.fromDate.getTime()) {
      setFromDate(resolved.fromDate);
    }
    if (!toDate || toDate.getTime() !== resolved.toDate.getTime()) {
      setToDate(resolved.toDate);
    }
  }, [dateBounds, fromDate, toDate]);

  const activePreset = useMemo(
    () =>
      findActivePresetForDateRange(
        fromDate,
        toDate,
        availableSeasons,
        dateBounds
      ),
    [fromDate, toDate, availableSeasons, dateBounds]
  );

  const handlePresetChange = (preset) => {
    const range = getDateRangeForPreset(preset, availableSeasons, dateBounds);
    if (!range) return;
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const handleFromDateChange = (date) => {
    if (!date) return;
    setFromDate(date);
    setToDate((prev) => (prev && moment(date).isAfter(prev) ? date : prev));
  };

  const handleToDateChange = (date) => {
    if (!date) return;
    setToDate(date);
    setFromDate((prev) => (prev && moment(date).isBefore(prev) ? date : prev));
  };

  const filterLabel = useMemo(
    () => formatDateRangeLabel(fromDate, toDate),
    [fromDate, toDate]
  );

  const fileFilterLabel = filterLabel;

  const handleMatchExportReadyChange = useCallback((ready) => {
    setIsMatchDataLoaded(ready);
  }, []);

  const isExportDisabled =
    !hasTeamData || isPageLoading || !isMatchDataLoaded;

  const buildExportPayload = useCallback(() => {
    const matches = matchTableRef.current?.getExportData?.() || [];

    return {
      teamName,
      gender: selectedGender,
      filterLabel,
      fileFilterLabel,
      matches,
      rankingPointsData: filteredChartData.points,
      rankingPositionData: filteredChartData.rank,
    };
  }, [
    teamName,
    selectedGender,
    filterLabel,
    fileFilterLabel,
    filteredChartData.points,
    filteredChartData.rank,
  ]);

  const handleExportClick = () => {
    if (isExportDisabled) return;
    setExportErrorMessage("");
    setIsExportModalOpen(true);
  };

  const handleCompareClick = () => {
    const teamCode = getTeamCode();
    if (!teamCode) return;
    navigate(
      `/team-comparison?team=${encodeURIComponent(teamCode)}&gender=${genderValue}`
    );
  };

  const handleCloseExportModal = () => {
    if (!isExportLoading) {
      setExportErrorMessage("");
      setIsExportModalOpen(false);
    }
  };

  const handleExtractXlsxClick = () => {
    setIsExportLoading(true);
    setExportErrorMessage("");
    try {
      exportTeamHistoryToXlsx(buildExportPayload());
    } catch (exportError) {
      console.error("Error exporting team history XLSX:", exportError);
      setExportErrorMessage(
        "Failed to export XLSX. Please try again or contact support."
      );
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleExtractPdfClick = () => {
    if (isExportLoading) return;

    setIsExportLoading(true);
    setExportErrorMessage("");
    setIsPdfExportMode(true);

    setTimeout(async () => {
      await waitForNextPaint();

      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch (fontError) {
          console.warn("Unable to wait for document fonts before PDF export:", fontError);
        }
      }

      const captureElement = exportCaptureRef.current;

      if (!captureElement) {
        console.error("Team history capture element not available");
        setExportErrorMessage(
          "Unable to prepare the page for export. Please refresh and try again."
        );
        setIsExportLoading(false);
        setIsPdfExportMode(false);
        return;
      }

      const imageLoadResult = await waitForImagesToSettle(captureElement);
      if (imageLoadResult.failed > 0) {
        console.warn(
          "Some images failed to load before team history PDF capture:",
          imageLoadResult
        );
      }

      await waitForNextPaint();
      await new Promise((resolve) => setTimeout(resolve, 300));

      try {
        await exportTeamHistoryToPdf({
          captureElement,
          teamName,
          gender: selectedGender,
          fileFilterLabel,
          svgLogoPath: FIVBLogo2,
        });
        setIsExportModalOpen(false);
      } catch (exportError) {
        console.error("Error exporting team history PDF:", exportError);
        setExportErrorMessage(
          exportError?.message ||
            "Failed to export PDF. Please try again or contact support."
        );
      } finally {
        setIsExportLoading(false);
        setIsPdfExportMode(false);
      }
    }, 500);
  };

  const renderTeamHistoryBody = ({
    useMobileLayout,
    exportMode,
    tableRef,
    onExportReadyChange,
  }) => (
    <>
      <Box data-team-history-export-section="filter-summary">
        <SeasonRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          minDate={dateBounds?.minDate || null}
          maxDate={dateBounds?.maxDate || null}
          activePreset={activePreset}
          onPresetChange={handlePresetChange}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          disabled={isPageLoading || isPdfExportMode}
        />
        {useMobileLayout ? (
          <MatchesPlayTableMobile
            teamName={teamName}
            gender={genderValue}
            historyRange={historyRange}
          />
        ) : (
          <MatchesPlayTable
            teamName={teamName}
            gender={genderValue}
            historyRange={historyRange}
          />
        )}
      </Box>
      <Box className="chartsWfiltersBox" data-team-history-export-section="charts">
        <HistoricalInfoBoxes
          netWrChange={netWrChange}
          rankChange={rankChange}
        />
        <Box className="chartsBox">
          <Box className="SingleChart">
            <div className="ChartTitleTypo">RANKING POINTS</div>
            <RankPointsChart
              teamName={teamName}
              apiDate={apiDate}
              gender={genderValue}
              historyRange={historyRange}
              exportMode={exportMode}
            />
          </Box>
          <Box className="SingleChart">
            <div className="ChartTitleTypo">RANKING POSITION</div>
            <RankPositionChart
              teamName={teamName}
              apiDate={apiDate}
              gender={genderValue}
              historyRange={historyRange}
              exportMode={exportMode}
            />
          </Box>
        </Box>
      </Box>
      <Box
        className="TDMatchHistorySection"
        data-team-history-export-section="match-history"
      >
        {useMobileLayout ? (
          <MatchProbTableMobile
            ref={tableRef}
            teamName={teamName}
            gender={genderValue}
            historyRange={historyRange}
            onExportReadyChange={onExportReadyChange}
          />
        ) : (
          <MatchProbTable
            ref={tableRef}
            teamName={teamName}
            gender={genderValue}
            historyRange={historyRange}
            onExportReadyChange={onExportReadyChange}
          />
        )}
      </Box>
    </>
  );

  return (
    <Box>
      <Box
        ref={exportCaptureRef}
        data-team-history-export-root="true"
        data-pdf-export-mode={isPdfExportMode ? "true" : undefined}
        className={`TDExportCapture${
          isPdfExportMode ? " TDExportCapture--exporting" : ""
        }`}
      >
        <Header
          teamName={teamName}
          rank={rank}
          points={points}
          confederationName={confederationName}
          confederationCode={confederationCode}
          kpiMetrics={kpiMetrics}
          onCompareClick={handleCompareClick}
          isCompareDisabled={!hasTeamData || isPageLoading}
          onExportClick={handleExportClick}
          isExportDisabled={isExportDisabled}
          hideExportButton={isPdfExportMode}
        />
        {error && <div>Error: {error}</div>}
        <Box className="BoxTD">
          {hasTeamData ? (
            renderTeamHistoryBody({
              useMobileLayout: isPdfExportMode ? false : isMobile,
              exportMode: isPdfExportMode,
              tableRef: matchTableRef,
              onExportReadyChange: handleMatchExportReadyChange,
            })
          ) : (
            <Box className="TDNoDataContainer">
              <Box className="TDNoDataCard">
                <div className="TDNoDataIcon" />
                <div className="TDNoDataTitle">No stats available for this team.</div>
                <button
                  type="button"
                  className="TDNoDataButton"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/world-ranking")}
                >
                  Go back
                </button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <ExtractReportModal
        open={isExportModalOpen}
        onClose={handleCloseExportModal}
        onExtractPdf={handleExtractPdfClick}
        onExtractXlsx={handleExtractXlsxClick}
        isLoading={isExportLoading}
        errorMessage={exportErrorMessage}
        title="Export Team History"
        description="Choose the format in which you'd like to export team history"
        ariaLabel="Export Team History"
      />
    </Box>
  );
};

export default TeamDetailLive;
